import React from "react";

type Props = {
  /** The option texts typed above, in order. */
  options?: (string | undefined | null)[];
  /** Form.Item injects these — the value is the correct option's text. */
  value?: string;
  onChange?: (value: string | undefined) => void;
};

/**
 * Picks the correct MCQ answer from the options that were typed above instead
 * of making the examiner retype it. The stored value stays the option *text*,
 * which is what the exam service grades against.
 */
// The exam service compares answers case- and whitespace-insensitively, so the
// picker has to consider "Test Two" and "test two " the same option.
const normalize = (text: string | undefined | null) =>
  String(text ?? "")
    .trim()
    .toLowerCase();

const CorrectOptionPicker: React.FC<Props> = ({
  options = [],
  value,
  onChange,
}: Props) => {
  const filled = (options || [])
    .map((option, index) => ({ text: String(option ?? "").trim(), index }))
    .filter((option) => option.text);

  if (!filled.length) {
    return (
      <div className="correct_option_empty">
        Fill in the options above, then pick the correct one here.
      </div>
    );
  }

  const duplicated = filled.some(
    (option, i) =>
      filled.findIndex((o) => normalize(o.text) === normalize(option.text)) !== i
  );

  // A saved question can carry an answer no option holds any more — say the
  // question was switched from flag to MCQ, or the options were reworded.
  const orphaned =
    Boolean(normalize(value)) &&
    !filled.some((option) => normalize(option.text) === normalize(value));

  return (
    <>
      <div className="correct_option_picker" role="radiogroup">
        {filled.map(({ text, index }) => {
          const selected = normalize(value) === normalize(text);
          return (
            <button
              type="button"
              role="radio"
              aria-checked={selected}
              key={index}
              title={text}
              className={
                selected
                  ? "correct_option_item correct_option_item--selected"
                  : "correct_option_item"
              }
              onClick={() => onChange?.(text)}
            >
              <span className="correct_option_radio" />
              <span className="correct_option_text">
                <span className="correct_option_index">{`Option ${
                  index + 1
                }`}</span>
                <span className="correct_option_label">{text}</span>
              </span>
            </button>
          );
        })}
      </div>
      {orphaned ? (
        <div className="correct_option_warning">
          {`The saved answer "${value}" is not one of the options above — pick the correct option.`}
        </div>
      ) : null}
      {duplicated ? (
        <div className="correct_option_warning">
          Two options have the same text — make them unique so the answer is not
          ambiguous.
        </div>
      ) : null}
    </>
  );
};

export default CorrectOptionPicker;
